import { useState, useMemo, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { toast } from 'react-toastify'
import { Loader2, AlertCircle, ChevronDown, ExternalLink, Plus, Bell, Layers } from 'lucide-react'
import { Sidebar } from '@/shared/components'
import { KanbanBoard, ActionModal, getActionsFromFeatures } from '../components'
import type { ActionItem } from '../components'
import { useProjectByShortId, useFeatures, useTeam } from '../hooks'
import { ProjectPendingPage } from './project-pending-page'
import { ProjectScopingPage } from './project-scoping-page'
import { ProjectQuotingPage } from './project-quoting-page'
import type { FeatureWithActions } from '@/shared/types'

function ProjectStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()

  const styles: Record<string, { bg: string; text: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-600' },
    pending: { bg: 'bg-slate-100', text: 'text-slate-600' },
    scoping: { bg: 'bg-violet-100', text: 'text-violet-600' },
    quoting: { bg: 'bg-amber-100', text: 'text-amber-600' },
    paused: { bg: 'bg-slate-100', text: 'text-slate-500' },
    completed: { bg: 'bg-blue-100', text: 'text-blue-600' },
  }

  const style = styles[status] || styles.active

  return (
    <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${style.bg} ${style.text}`}>
      {t(`team.status.${status}`)}
    </span>
  )
}

export function ProjectKanbanPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isActionModalOpen, setIsActionModalOpen] = useState(false)

  const { data: project, isLoading: isLoadingProject, error: projectError } = useProjectByShortId(shortId)
  const { data: team, isLoading: isLoadingTeam } = useTeam(project?.team_id)
  const { data: features, isLoading: isLoadingFeatures, error: featuresError } = useFeatures(project?.id)

  const isLoading = isLoadingProject || isLoadingTeam || isLoadingFeatures
  const error = projectError || featuresError

  // Show toast when arriving from payment success
  useEffect(() => {
    const activated = searchParams.get('activated')
    // HOT-010: Check project.status for active (kanban enabled)
    if (activated === 'true' && project?.status === 'active') {
      toast.success(t('kanban.projectActivated'))
      // Remove the query param to avoid showing toast on refresh
      searchParams.delete('activated')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, project?.status, t])

  // Calculate actions from features (must be before conditional returns)
  const actions = useMemo(() => {
    if (!features) return []
    return getActionsFromFeatures(features)
  }, [features])

  // If project is pending, show the pending page instead of kanban
  if (project?.status === 'pending') {
    return <ProjectPendingPage />
  }

  // HOT-010: scoping/quoting statuses are on PROJECT, not TEAM
  // If project is in scoping phase, show the scoping page
  if (project && team && project.status === 'scoping') {
    return <ProjectScopingPage project={project} team={team} />
  }

  // If project is in quoting phase, show the quoting page
  if (project && team && project.status === 'quoting') {
    return <ProjectQuotingPage project={project} team={team} />
  }

  const handleFeatureClick = (feature: FeatureWithActions) => {
    navigate(`/f/${feature.short_id}`)
  }

  const handleActionClick = (action: ActionItem) => {
    setIsActionModalOpen(false)
    navigate(`/f/${action.featureShortId}`)
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-hidden bg-background-secondary">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6">
          {/* Left side: Project selector + Status badge */}
          <div className="flex items-center gap-4">
            {/* Project Selector */}
            <button className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 transition-colors hover:bg-background-secondary">
              <span className="text-base font-semibold text-foreground">
                {project?.name || t('kanban.title')}
              </span>
              <ChevronDown className="h-[18px] w-[18px] text-foreground-muted" />
            </button>

            {/* Project Status Badge */}
            <ProjectStatusBadge status={project?.status || 'active'} />
          </div>

          {/* Right side: Actions + Test env link + New Feature button */}
          <div className="flex items-center gap-3">
            {/* Action Badge - only show if there are actions */}
            {actions.length > 0 && (
              <button
                onClick={() => setIsActionModalOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-orange-600 transition-colors hover:bg-orange-100"
              >
                <Bell className="h-4 w-4" />
                <span className="text-[13px] font-semibold">{actions.length}</span>
              </button>
            )}

            {/* Test Environment Link */}
            <button className="flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-2 text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground">
              <ExternalLink className="h-4 w-4" />
              <span className="text-[13px] font-medium">{t('kanban.testEnvironment')}</span>
            </button>

            {/* New Feature Button */}
            <button className="flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-white transition-colors hover:bg-accent/90">
              <Plus className="h-4 w-4" />
              <span className="text-[13px] font-semibold">{t('kanban.newFeature')}</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-8 pb-6">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-destructive">
              <AlertCircle className="h-8 w-8" />
              <p>{t('common.error')}</p>
            </div>
          ) : features && features.length === 0 ? (
            // Empty state when project is active but no features yet
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/10">
                <Layers className="h-10 w-10 text-accent" />
              </div>
              <div className="max-w-sm">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('kanban.emptyTitle')}
                </h2>
                <p className="mt-2 text-sm text-foreground-muted">
                  {t('kanban.emptyDescription')}
                </p>
              </div>
            </div>
          ) : (
            <KanbanBoard
              features={features || []}
              onFeatureClick={handleFeatureClick}
            />
          )}
        </div>
      </main>

      {/* Action Modal */}
      <ActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        actions={actions}
        onActionClick={handleActionClick}
      />
    </div>
  )
}
